import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import Loader from 'eventtia-ui-components/lib/Loader';
import clsx from 'clsx';
import callApi from '../../actions/callApi';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import SubpageTitle from '../../components/SubpageTitle';
import ArrowLeft from '../../assets/arrowLeft.svg';
import DownloadIcon from '../../assets/DownloadIcon';
import config from './config';
import PhotoGroupCover from './PhotoGroupCover';

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: 15,
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  grid: {
    paddingBottom: theme.spacing(6),
  },
  imgButton: {
    all: 'unset',
    cursor: 'pointer',
    width: '100%',
    height: '100%',
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    boxShadow: '0 4px 4px rgba(0, 0, 0, 0.25)',
  },
  backButton: {
    width: 45,
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(6.25),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(8),
    },
  },
  contentWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  prevButton: {
    width: 45,
  },
  nextButton: {
    width: 45,
    '& img': {
      transform: 'rotate(180deg)',
    },
  },
  disabled: {
    opacity: 0.3,
  },
  imgWrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
    '&:hover $downloadButton': {
      opacity: 1,
    },
  },
  downloadButton: {
    position: 'absolute',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    boxShadow: '0 4px 4px rgba(0, 0, 0, 0.4)',
    top: theme.spacing(-4),
    right: theme.spacing(-4),
    zIndex: 1,
    opacity: 0,
    transition: 'opacity 0.5s',
    '&:hover': {
      backgroundColor: 'white',
    },
  },
}));

const ITEMS_PER_PAGE = 4;

const getWidth = (index) => {
  if (Math.floor(index / 2) % 2 === 0) return (index % 2 === 0) ? 4 : 8;
  return (index % 2 === 0) ? 8 : 4;
};

const PhotoGroupContent = ({
  group,
  closeGroup,
  eventFiles,
  callApi: dispatchCallApi,
  fetchStatus,
}) => {
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(1);
  const {
    isFetching,
    order = [],
  } = fetchStatus;
  const content = order ? order.map((id) => eventFiles[id].urls) : [];

  const fileCategoryId = config[group].categoryId;

  useEffect(() => {
    dispatchCallApi('categoryFiles', { fileCategoryId });
  }, [fileCategoryId]);

  const paginatedContent = content.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const hasMultiplePages = order?.length > ITEMS_PER_PAGE;
  const prevEnabled = currentPage > 1 && !isFetching;
  const nextEnabled = hasMultiplePages && currentPage * ITEMS_PER_PAGE < content.length
    && !isFetching;

  const handleNextPage = () => {
    if (nextEnabled) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (prevEnabled) setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  return (
    <>
      <div className={classes.titleWrapper}>
        <IconButton
          className={classes.backButton}
          onClick={closeGroup}
          color="primary"
        >
          <img src={ArrowLeft} alt="go back" />
        </IconButton>
        <SubpageTitle text="Photos" />
      </div>
      <Typography className={classes.sectionTitle} variant="subtitle2">
        2026 AWARDS WEEK
      </Typography>
      <br />
      <br />
      <div className={classes.contentWrapper}>
        {hasMultiplePages && (
          <IconButton
            className={clsx(
              classes.prevButton,
              !prevEnabled && classes.disabled
            )}
            onClick={handlePrevPage}
            color="primary"
            disabled={!prevEnabled}
          >
            <img src={ArrowLeft} alt="previous" />
          </IconButton>
        )}
        <Grid container spacing={1} className={classes.grid}>
          <Grid item xs={12}>
            <PhotoGroupCover
              group={group}
              topText={config[group].topText}
              mainText={config[group].mainText}
              bottomText={config[group].bottomText}
              height={241}
            />
          </Grid>
          {!!paginatedContent.length
          && paginatedContent.map((pic, index) => (
            <Grid key={pic.medium} item sm={getWidth(index)} xs={12}>
              <div className={classes.imgWrapper}>
                <IconButton
                  className={classes.downloadButton}
                  onClick={() => {
                    fetch(pic.original)
                      .then((response) => response.blob())
                      .then((blob) => {
                        const objectUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = objectUrl;
                        link.setAttribute('download', pic.filename);
                        document.body.appendChild(link);
                        link.click();
                        link.parentNode.removeChild(link);
                      });
                  }}
                  color="primary"
                >
                  <DownloadIcon />
                </IconButton>
                <img
                  src={pic.medium}
                  alt=""
                  className={classes.photo}
                  style={{ height: 290 }}
                />
              </div>
            </Grid>
          ))}
          {isFetching && (
            <Grid item xs={12}>
              <Loader loading />
            </Grid>
          )}
        </Grid>
        {hasMultiplePages && (
          <IconButton
            className={clsx(
              classes.nextButton,
              !nextEnabled && classes.disabled
            )}
            onClick={handleNextPage}
            color="primary"
            disabled={!nextEnabled}
          >
            <img src={ArrowLeft} alt="next" />
          </IconButton>
        )}
      </div>
    </>
  );
};

PhotoGroupContent.propTypes = {
  group: PropTypes.string.isRequired,
  closeGroup: PropTypes.func.isRequired,
  callApi: PropTypes.func.isRequired,
  eventFiles: PropTypes.objectOf(CustomPropTypes.eventFile),
  fetchStatus: CustomPropTypes.fetchStatus.isRequired,
};

PhotoGroupContent.defaultProps = {
  eventFiles: undefined,
};

const mapStateToProps = ({
  entities: { eventFiles },
  fetchStatus: { categoryFiles: fetchStatus },
}) => ({
  eventFiles,
  fetchStatus,
});

export default connect(mapStateToProps, { callApi })(PhotoGroupContent);
